import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Button from '../../../../components/UI/Button/Button';
import {
  generateSummary,
  GenerateSummaryPayload,
} from '../../../../socket/publishers/generateSummary';
import { RootState } from '../../../../store';
import styles from './SummaryResult.module.scss';
import { SummaryUpdateStatus } from '../../../../socket/subscriptions/summaryUpdateHandler';

const SummaryResult: React.FC = () => {
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    companyId,
    prompt,
    maxCharsPerSection,
    summaryText,
    summaryMetadata,
    summaryStatus,
    summaryError,
    summaryGeneratedAt,
    selectedDocuments,
    model,
    summaryType,
    promptName,
    aggregationOptions,
  } = useSelector((state: RootState) => state.summaryTest);

  const selectedDocIds = Object.keys(selectedDocuments);
  const hasAnySections = selectedDocIds.some(
    (docId) => (selectedDocuments[docId]?.selectedSectionIdx?.length ?? 0) > 0,
  );

  // Reset isGenerating when summary generation completes or errors
  useEffect(() => {
    if (
      summaryStatus === SummaryUpdateStatus.Complete ||
      summaryStatus === SummaryUpdateStatus.Error
    ) {
      setIsGenerating(false);
    }
  }, [summaryStatus]);

  const loading = summaryStatus === SummaryUpdateStatus.Loading || isGenerating;

  const handleGenerate = async () => {
    if (!companyId) return;

    if (summaryType === 'predefined' && !promptName) return;

    if (
      summaryType === 'custom' &&
      (!prompt.trim() || !hasAnySections || selectedDocIds.length === 0)
    ) {
      return;
    }

    setIsGenerating(true);

    try {
      let request: GenerateSummaryPayload;
      if (summaryType === 'predefined' && promptName) {
        request = {
          type: 'predefined',
          companyId,
          promptName,
        };
      } else {
        request = {
          type: 'custom',
          companyId,
          prompt: prompt.trim(),
          maxCharsPerSection,
          selectedDocuments,
          model,
          aggregationOptions,
        };
      }

      const response = await generateSummary(request);

      if (!response.success) {
        // Error will be handled by the WebSocket handler
        setIsGenerating(false);
        return;
      }
      // If successful, the WebSocket handler will update Redux and the useEffect will reset isGenerating
    } catch (err: any) {
      console.error('Error starting summary generation:', err);
      setIsGenerating(false);
      // Error will be handled by the WebSocket handler
    }
  };

  const handleCopy = () => {
    if (summaryText) {
      navigator.clipboard.writeText(summaryText);
    }
  };

  const handleDownload = () => {
    if (summaryText) {
      const blob = new Blob([summaryText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `summary-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const wordCount = summaryText
    ? summaryText
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0).length
    : 0;
  const charCount = summaryText ? summaryText.length : 0;

  const canGenerate =
    companyId &&
    (summaryType === 'custom'
      ? prompt.trim().length > 0 && hasAnySections && selectedDocIds.length > 0
      : !!promptName);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Summary Result</h2>
        <div className={styles.controls}>
          {summaryText && (
            <>
              <Button variant="secondary" size="small" onClick={handleCopy}>
                Copy
              </Button>
              <Button variant="secondary" size="small" onClick={handleDownload}>
                Download
              </Button>
            </>
          )}
        </div>
      </div>

      <div className={styles.generateButton}>
        <Button
          variant="primary"
          size="large"
          onClick={handleGenerate}
          disabled={loading || !canGenerate}
          loading={loading}
          fullWidth
        >
          {loading ? 'Generating Summary...' : 'Generate Summary'}
        </Button>
      </div>

      {summaryError && (
        <div className={styles.errorContainer}>
          <strong>Error:</strong> {summaryError}
        </div>
      )}

      {loading && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingText}>
            Generating summary using AI model...
            <br />
            This may take a few moments.
          </div>
        </div>
      )}

      {!loading && !summaryText && !summaryError && (
        <div className={styles.emptyState}>
          {!canGenerate
            ? 'Please select a company, documents, sections, and provide a prompt to generate a summary'
            : 'Click "Generate Summary" to create an AI-powered analysis of the selected content'}
        </div>
      )}

      {!loading && summaryText && (
        <div className={styles.resultContainer}>
          <div className={styles.resultHeader}>
            <div className={styles.resultInfo}>
              Generated on {summaryGeneratedAt?.toLocaleString()} |
              <span className={styles.wordCount}> {wordCount} words</span> |
              {charCount.toLocaleString()} characters
              {summaryMetadata && (
                <>
                  |{' '}
                  <span className={styles.tokenCount}>
                    {' '}
                    {summaryMetadata.totalTokens.toLocaleString()} tokens
                  </span>{' '}
                  |{summaryMetadata.generationTime}ms
                </>
              )}
            </div>
            <div className={styles.resultActions}>
              <Button variant="secondary" size="small" onClick={handleGenerate} disabled={loading}>
                Regenerate
              </Button>
            </div>
          </div>

          <div className={styles.resultContent}>
            <div className={styles.summaryText}>{summaryText}</div>
          </div>

          <div className={styles.resultFooter}>
            Summary generated from {summaryMetadata?.sectionCount ?? '...'} document section
            {summaryMetadata?.sectionCount !== 1 ? 's' : ''}
            {summaryMetadata && (
              <>
                {' '}
                for {summaryMetadata.companyName} ({summaryMetadata.documentCount} document
                {summaryMetadata.documentCount !== 1 ? 's' : ''})
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryResult;
